import React, { useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowDownTrayIcon, ChartBarIcon, ExclamationTriangleIcon, ArrowPathIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2pdf from 'html2pdf.js';
import { pctSymbol } from '../../utils/formatters';

// Try to import SuccessionThemeProvider, fall back gracefully if not available
let useSuccessionTheme;
try {
  const SuccessionThemeModule = require('./SuccessionThemeProvider');
  useSuccessionTheme = SuccessionThemeModule.useSuccessionTheme;
} catch (error) {
  // SuccessionThemeProvider not available, we'll handle this gracefully
  useSuccessionTheme = null;
}

// New SectionReadinessScores component for displaying section scores
const SectionReadinessScores = ({ formData }) => {
  // Parse form data if it's a string
  const parsedData = typeof formData === 'string' ? JSON.parse(formData) : formData;

  // Calculate section scores with more advanced weighting
  const calculateSectionScore = (sectionId) => {
    // Default score if section doesn't exist or is empty
    if (!parsedData || !parsedData[sectionId]) return 0;
    
    const sectionData = parsedData[sectionId];
    
    // Calculate filled fields ratio
    const fields = Object.keys(sectionData);
    if (fields.length === 0) return 0;
    
    // Points available and earned for scoring
    let totalAvailablePoints = 0;
    let totalEarnedPoints = 0;
    
    fields.forEach(field => {
      const value = sectionData[field];
      
      // Skip fields that are internal or metadata
      if (field.startsWith('_') || field === 'id') return;
      
      // Different scoring based on value type
      if (value !== undefined && value !== null) {
        // Text responses (evaluate depth and specificity)
        if (typeof value === 'string') {
          // Empty strings get no points
          if (value.trim() === '') {
            totalAvailablePoints += 10;
          } 
          // Basic scoring based on length and specificity
          else {
            totalAvailablePoints += 10;
            
            // Calculate points based on text length and quality
            const wordCount = value.split(/\s+/).length;
            let textPoints = 0;
            
            // Word count scoring (0-7 points)
            if (wordCount >= 100) textPoints = 7;
            else if (wordCount >= 50) textPoints = 5;
            else if (wordCount >= 30) textPoints = 4;
            else if (wordCount >= 20) textPoints = 3;
            else if (wordCount >= 10) textPoints = 2;
            else if (wordCount >= 5) textPoints = 1;
            
            // Check for specificity markers (numbers, percentages, specific terms)
            // This is a simplified approach - in a real NLP system this would be more sophisticated
            const hasSpecifics = /\d+%|\$\d+|\d+ years|specific|concrete|detailed|timeline|plan/i.test(value);
            if (hasSpecifics) textPoints += 3;
            
            totalEarnedPoints += Math.min(10, textPoints);
          }
        }
        // Checkbox/multiple choice responses
        else if (Array.isArray(value)) {
          totalAvailablePoints += 10;
          // More selected options indicate more thought put into the response
          // Scale based on having at least some selections
          if (value.length > 0) {
            // Arbitrary scaling for array length
            totalEarnedPoints += Math.min(10, value.length * 2 + 4);
          }
        }
        // Boolean or single-choice responses
        else if (typeof value === 'boolean' || (typeof value === 'string' && ['yes', 'no', 'maybe', 'partially', 'in_progress'].includes(value.toLowerCase()))) {
          totalAvailablePoints += 10;
          
          // Assign points based on response
          // "Yes" and true values get full points as they indicate readiness
          if (value === true || value === 'yes' || value === 'completed') {
            totalEarnedPoints += 10;
          }
          // Partial/in-progress responses get partial points
          else if (value === 'partially' || value === 'in_progress' || value === 'somewhat' || value === 'maybe') {
            totalEarnedPoints += 5;
          }
          // "No" gets no points but shows awareness
          else if (value === false || value === 'no' || value === 'not_started') {
            totalEarnedPoints += 2; // Some points for awareness
          }
        }
        // Nested objects (evaluate recursively)
        else if (typeof value === 'object') {
          // Add points for having structured data
          const nestedFields = Object.keys(value);
          totalAvailablePoints += 10 * nestedFields.length;
          
          // Check each field in the nested object
          nestedFields.forEach(nestedField => {
            const nestedValue = value[nestedField];
            if (nestedValue) {
              if (typeof nestedValue === 'string' && nestedValue.trim() !== '') {
                totalEarnedPoints += 10;
              } else if (Array.isArray(nestedValue) && nestedValue.length > 0) {
                totalEarnedPoints += 10;
              } else if (typeof nestedValue === 'object' && Object.keys(nestedValue).length > 0) {
                totalEarnedPoints += 10;
              } else if (typeof nestedValue === 'number' || typeof nestedValue === 'boolean') {
                totalEarnedPoints += 10;
              }
            }
          });
        }
        // Other types (numbers, etc.)
        else {
          totalAvailablePoints += 10;
          totalEarnedPoints += 10; // Full credit for having a value
        }
      } else {
        // Field exists but has no value
        totalAvailablePoints += 10;
      }
    });
    
    // Calculate score as percentage (with minimum of 0 and maximum of 100)
    return totalAvailablePoints > 0
      ? Math.min(100, Math.max(0, Math.round((totalEarnedPoints / totalAvailablePoints) * 100)))
      : 0;
  };

  // Section mappings from schema to formData
  const sectionMappings = {
    'personal_goals': 'personalGoals',
    'current_status': 'current_status',
    'planning_barriers': 'planning_barriers',
    'selling_motivations': 'selling_motivations',
    'business_assessment': 'businessStructure',
    'emotional_readiness': 'emotionalReadiness',
    'business_valuation': 'business_valuation',
    'business_preparation': 'business_preparation',
    'team_client_considerations': 'team_client_considerations',
    'successor_considerations': 'successor_considerations',
    'post_succession': 'post_succession',
    'deal_structure': 'deal_structure',
    // Alternative mappings to check for different data structures
    'goals': 'personal_goals',
    'readiness': 'emotional_readiness',
    'businessInfo': 'personalInformation',
    'financialStatus': 'financial_status',
    'clientBase': 'client_base',
    'successionOptions': 'succession_options',
    'transitionPlanning': 'transition_planning',
    'nextSteps': 'next_steps'
  };

  // Section display names
  const sectionDisplayNames = {
    'personal_goals': 'Personal and Business Goals',
    'current_status': 'Current Succession Plan Status',
    'planning_barriers': 'Planning Barriers Assessment',
    'selling_motivations': 'Motivations for Selling',
    'business_assessment': 'Business Self-Assessment',
    'emotional_readiness': 'Emotional and Financial Readiness',
    'business_valuation': 'Business Valuation',
    'business_preparation': 'Business Transition Readiness',
    'team_client_considerations': 'Team and Client Considerations',
    'successor_considerations': 'Successor Considerations',
    'post_succession': 'Post-Succession Plans',
    'deal_structure': 'Deal Structure and Financing',
    // Additional mappings
    'goals': 'Personal and Business Goals',
    'readiness': 'Emotional and Financial Readiness',
    'businessInfo': 'Business Information',
    'financialStatus': 'Financial Status',
    'clientBase': 'Client Base',
    'successionOptions': 'Succession Options',
    'transitionPlanning': 'Transition Planning',
    'nextSteps': 'Next Steps'
  };

  // Generate section scores with handling for different data structures
  const sectionScores = {};
  let totalScore = 0;
  let activeSectionCount = 0;
  
  // First pass: Try direct mappings from schema
  Object.keys(sectionMappings).forEach(sectionId => {
    const formDataKey = sectionMappings[sectionId];
    
    // Skip duplicate mappings (alternative names for same sections)
    if (sectionId !== formDataKey && Object.values(sectionMappings).includes(sectionId)) {
      return;
    }
    
    // Try to calculate score for this section
    const score = calculateSectionScore(formDataKey);
    
    // Only include sections that have data (to avoid artificially lowering overall score)
    if (score > 0) {
      sectionScores[sectionId] = score;
      totalScore += score;
      activeSectionCount++;
    }
  });
  
  // Second pass: Look for sections directly in formData
  if (parsedData) {
    Object.keys(parsedData).forEach(key => {
      // Skip if we've already scored this section under a mapped name
      if (Object.values(sectionMappings).includes(key)) {
        return;
      }
      
      // Try to find a display name for this section
      const sectionId = Object.keys(sectionMappings).find(id => sectionMappings[id] === key) || key;
      const displayName = sectionDisplayNames[sectionId] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      
      // Calculate score for this section
      const score = calculateSectionScore(key);
      
      // Only include sections that have data
      if (score > 0 && !sectionScores[sectionId]) {
        sectionScores[sectionId] = score;
        sectionDisplayNames[sectionId] = displayName;
        totalScore += score;
        activeSectionCount++;
      }
    });
  }

  // Calculate overall score
  const overallScore = activeSectionCount > 0 ? Math.round(totalScore / activeSectionCount) : 0;

  // Color mapping based on score
  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-yellow-500';
    if (score >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-blue-100';
    if (score >= 40) return 'bg-yellow-100';
    if (score >= 20) return 'bg-orange-100';
    return 'bg-red-100';
  };

  const getScoreTextColor = (score) => {
    if (score >= 80) return 'text-green-800';
    if (score >= 60) return 'text-blue-800';
    if (score >= 40) return 'text-yellow-800';
    if (score >= 20) return 'text-orange-800';
    return 'text-red-800';
  };

  // Get recommendations for sections that need improvement
  const getLowScoringRecommendations = () => {
    const lowScoringSections = Object.entries(sectionScores)
      .filter(([_, score]) => score < 60)
      .sort((a, b) => a[1] - b[1]); // Sort by score ascending
    
    if (lowScoringSections.length === 0) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mt-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Great job!</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  You've made excellent progress on your succession plan! Continue to revisit and update your plan as circumstances change.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Generate recommendations for up to 3 lowest scoring sections
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mt-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Recommended Focus Areas</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p className="mb-2">
                Consider focusing on these sections to strengthen your succession plan:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                {lowScoringSections.slice(0, 3).map(([sectionId, score]) => (
                  <li key={sectionId}>
                    <span className="font-medium">{sectionDisplayNames[sectionId] || sectionId}</span> ({score}%) - {getRecommendationForSection(sectionId, score)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Specific recommendations by section
  const getRecommendationForSection = (sectionId, score) => {
    const recommendations = {
      'personal_goals': 'Define clearer short, medium, and long-term goals for both personal and business aspects.',
      'current_status': 'Document your current succession plan status and identify specific benefits you hope to achieve.',
      'planning_barriers': "Honestly assess what's been holding you back from succession planning.",
      'selling_motivations': 'Explore and document your primary motivations for considering a business transition.',
      'business_assessment': "Conduct a thorough self-assessment of your business from a buyer's perspective.",
      'emotional_readiness': 'Work on your emotional attachment to the business and prepare financially for transition.',
      'business_valuation': 'Get a professional valuation and document key business metrics that impact value.',
      'business_preparation': 'Implement systems and processes that make your business more transferable.',
      'team_client_considerations': 'Develop specific plans for communicating with and transitioning team members and clients.',
      'successor_considerations': 'Identify and evaluate potential successors, and define clear criteria for your ideal candidate.',
      'post_succession': 'Create a detailed plan for your life and involvement after transition.',
      'deal_structure': 'Consult with financial advisors on optimal deal structures and tax implications.'
    };
    
    return recommendations[sectionId] || 'Provide more detailed information in this area to improve your score.';
  };

  return (
    <div className="mt-4">
      {/* Overall Score */}
      <div className="mb-6 text-center">
        <div className="inline-block bg-white rounded-full p-1 shadow-lg">
          <div className={`rounded-full p-6 flex items-center justify-center ${getScoreBgColor(overallScore)}`}>
            <div className="text-center">
              <h2 className={`text-2xl font-bold ${getScoreTextColor(overallScore)}`}>Overall Readiness</h2>
              <div className="text-5xl font-bold mt-2 mb-1">{overallScore}<span className="text-xl">%</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations based on scores */}
      {getLowScoringRecommendations()}

      {/* Section Scores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {Object.entries(sectionScores).map(([sectionId, score]) => (
          <div key={sectionId} className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">{sectionDisplayNames[sectionId] || sectionId}</h3>
            <div className="flex items-center">
              <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                <div 
                  className={`h-2.5 rounded-full ${getScoreColor(score)}`} 
                  style={{ width: `${score}%` }}
                ></div>
              </div>
              <span className={`text-xs font-bold ${getScoreTextColor(score)}`}>
                {score}{pctSymbol()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReadinessScoreCard = ({ title, score, description, icon, bgColor, iconBgColor, textColor }) => {
  const Icon = icon;
  // Convert score to a percentage display (0-100)
  const displayScore = Math.min(100, Math.max(0, score));
  
  return (
    <div className={`${bgColor} overflow-hidden shadow rounded-lg`}>
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center">
          <div className={`flex-shrink-0 ${iconBgColor} rounded-md p-3`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <h3 className={`text-sm font-medium ${textColor}`}>{title}</h3>
            <div className="mt-4">
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full ${textColor} bg-white bg-opacity-30`}>
                    {displayScore}/100
                  </div>
                </div>
                <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-white bg-opacity-20">
                  <div style={{ width: `${displayScore}%` }} className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-white`}></div>
                </div>
              </div>
            </div>
            <p className={`mt-2 text-sm ${textColor} opacity-80`}>
              {description || "No description available"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const InsightCard = ({ title, content, icon, bgColor, iconBgColor, textColor }) => {
  const Icon = icon;
  
  return (
    <div className={`${bgColor} overflow-hidden shadow rounded-lg`}>
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center">
          <div className={`flex-shrink-0 ${iconBgColor} rounded-md p-3`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <h3 className={`text-sm font-medium ${textColor}`}>{title}</h3>
            <p className={`mt-2 text-sm ${textColor} opacity-80`}>
              {content || "No insight available"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const SuccessionPlanningSummaryPage = ({ formData, onRevise, aiInsights }) => {
  const pdfRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  
  // Theme setup with graceful fallback
  let colors, themeColors;
  
  if (useSuccessionTheme) {
    try {
      const { theme, isLightTheme, isDarkTheme, company } = useSuccessionTheme();
      
      // Create color object with actual company colors
      colors = {
        primary: company?.primary_color || '#5489B6',
        secondary: company?.secondary_color || '#002949',
        accent: company?.accent_color || '#5B5E5A',
        neutral: company?.neutral_color || '#666666',
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
      };

      // Create theme-aware color system
      themeColors = {
        bgPrimary: isDarkTheme ? '#1f2937' : '#ffffff',
        bgSecondary: isDarkTheme ? '#111827' : '#f9fafb',
        bgTertiary: isDarkTheme ? '#374151' : '#e5e7eb',
        textPrimary: isDarkTheme ? '#f9fafb' : '#111827',
        textSecondary: isDarkTheme ? '#d1d5db' : '#6b7280',
        textMuted: isDarkTheme ? '#9ca3af' : '#9ca3af',
        border: isDarkTheme ? '#374151' : '#e5e7eb',
        shadow: isDarkTheme ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        shadowLg: isDarkTheme ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      };
    } catch (error) {
      // Fallback to default values if theme context fails
      colors = null;
      themeColors = null;
    }
  }

  // Fallback colors when theme is not available
  const fallbackColors = {
    primary: '#5489B6',
    secondary: '#002949',
    accent: '#5B5E5A',
    neutral: '#666666',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    bgPrimary: '#ffffff',
    bgSecondary: '#f9fafb',
    bgTertiary: '#e5e7eb',
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    border: '#e5e7eb',
    shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  };

  // Use theme colors or fallback
  const finalColors = colors || fallbackColors;
  const finalThemeColors = themeColors || fallbackColors;
  
  // Debug logging for AI insights
  console.log("SuccessionPlanningSummaryPage received aiInsights:", aiInsights);
  
  // Generate summary content based on form data
  const generateSummaryContent = () => {
    // Parse form data if it's a string
    const parsedData = typeof formData === 'string' ? JSON.parse(formData) : formData;
    
    let content = '# Succession Planning Summary\n\n';
    
    // Add personal information summary
    if (parsedData.personalInformation) {
      content += '## Personal Information\n\n';
      
      if (parsedData.personalInformation.name) {
        content += `**Name:** ${parsedData.personalInformation.name}\n\n`;
      }
      
      if (parsedData.personalInformation.firm) {
        content += `**Firm:** ${parsedData.personalInformation.firm}\n\n`;
      }
      
      if (parsedData.personalInformation.title) {
        content += `**Title:** ${parsedData.personalInformation.title}\n\n`;
      }
      
      if (parsedData.personalInformation.yearsInBusiness) {
        content += `**Years in Business:** ${parsedData.personalInformation.yearsInBusiness}\n\n`;
      }
      
      if (parsedData.personalInformation.retirementTimeline) {
        content += `**Retirement Timeline:** ${parsedData.personalInformation.retirementTimeline}\n\n`;
      }
      
      if (parsedData.personalInformation.successorIdentified === 'yes' && parsedData.personalInformation.successorName) {
        content += `**Identified Successor:** ${parsedData.personalInformation.successorName} (${parsedData.personalInformation.successorRelationship || 'No relationship specified'})\n\n`;
      }
    }
    
    // Add business structure summary
    if (parsedData.businessStructure) {
      content += '## Business Structure\n\n';
      
      if (parsedData.businessStructure.entityType) {
        const entityTypeMap = {
          'sole': 'Sole Proprietorship',
          'partnership': 'Partnership',
          'llc': 'Limited Liability Company (LLC)',
          'corp': 'Corporation',
          's-corp': 'S Corporation'
        };
        content += `**Entity Type:** ${entityTypeMap[parsedData.businessStructure.entityType] || parsedData.businessStructure.entityType}\n\n`;
      }
      
      if (parsedData.businessStructure.ownershipStructure) {
        content += `**Ownership Structure:** ${parsedData.businessStructure.ownershipStructure}\n\n`;
      }
      
      if (parsedData.businessStructure.numberOfEmployees) {
        content += `**Number of Employees:** ${parsedData.businessStructure.numberOfEmployees}\n\n`;
      }
      
      if (parsedData.businessStructure.keyEmployees && parsedData.businessStructure.keyEmployees.length > 0) {
        content += `### Key Employees\n\n`;
        parsedData.businessStructure.keyEmployees.forEach(employee => {
          content += `- **${employee.name}:** ${employee.role} (${employee.yearsWithFirm} years with firm)\n`;
        });
        content += `\n`;
      }
    }
    
    // Add client base summary
    if (parsedData.clientBase) {
      content += '## Client Base\n\n';
      
      if (parsedData.clientBase.totalClients) {
        content += `**Total Clients:** ${parsedData.clientBase.totalClients}\n\n`;
      }
      
      if (parsedData.clientBase.clientsBySegment && parsedData.clientBase.clientsBySegment.length > 0) {
        content += `### Client Segments\n\n`;
        parsedData.clientBase.clientsBySegment.forEach(segment => {
          content += `- **${segment.segment}:** ${segment.count} clients (${segment.revenue} of revenue)\n`;
        });
        content += `\n`;
      }
      
      if (parsedData.clientBase.clientDemographics) {
        content += `**Client Demographics:** ${parsedData.clientBase.clientDemographics}\n\n`;
      }
    }
    
    // Add financial status summary
    if (parsedData.financialStatus) {
      content += '## Financial Status\n\n';
      
      if (parsedData.financialStatus.annualRevenue) {
        content += `**Annual Revenue:** ${parsedData.financialStatus.annualRevenue}\n\n`;
      }
      
      if (parsedData.financialStatus.aum) {
        content += `**Assets Under Management:** ${parsedData.financialStatus.aum}\n\n`;
      }
      
      if (parsedData.financialStatus.valuationEstimate) {
        content += `**Valuation Estimate:** ${parsedData.financialStatus.valuationEstimate}\n\n`;
      }
      
      if (parsedData.financialStatus.valuationMethod) {
        content += `**Valuation Method:** ${parsedData.financialStatus.valuationMethod}\n\n`;
      }
    }
    
    // Add succession options summary
    if (parsedData.successionOptions) {
      content += '## Succession Options\n\n';
      
      if (parsedData.successionOptions.preferredOption) {
        const optionMap = {
          'internal': 'Internal Succession',
          'external': 'External Sale',
          'family': 'Family Succession'
        };
        content += `**Preferred Option:** ${optionMap[parsedData.successionOptions.preferredOption] || parsedData.successionOptions.preferredOption}\n\n`;
      }
      
      if (parsedData.successionOptions.internalCandidates) {
        content += `**Internal Candidates:** ${parsedData.successionOptions.internalCandidates}\n\n`;
      }
      
      if (parsedData.successionOptions.timeframe) {
        content += `**Planned Timeframe:** ${parsedData.successionOptions.timeframe}\n\n`;
      }
      
      if (parsedData.successionOptions.dealStructure) {
        content += `**Deal Structure:** ${parsedData.successionOptions.dealStructure}\n\n`;
      }
    }
    
    // Add next steps
    if (parsedData.nextSteps) {
      content += '## Next Steps\n\n';
      
      if (parsedData.nextSteps.immediatePriorities) {
        content += `**Immediate Priorities:** ${parsedData.nextSteps.immediatePriorities}\n\n`;
      }
      
      if (parsedData.nextSteps.advisorTeam && parsedData.nextSteps.advisorTeam.length > 0) {
        content += `### Advisory Team\n\n`;
        parsedData.nextSteps.advisorTeam.forEach(advisor => {
          content += `- **${advisor.type}:** ${advisor.name} - ${advisor.involvement}\n`;
        });
        content += `\n`;
      }
    }
    
    return content;
  };
  
  const summaryContent = generateSummaryContent();
  
  const handleDownloadPDF = () => {
    const element = pdfRef.current;
    if (!element) {
      console.error('PDF content element not found');
      return;
    }

    try {
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: 'Succession_Planning_Roadmap.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };
      
      // Show loading state
      const loadingNotification = document.createElement('div');
      loadingNotification.className = 'fixed bottom-4 right-4 bg-blue-600 text-white py-2 px-4 rounded-md shadow';
      loadingNotification.textContent = 'Generating PDF...';
      document.body.appendChild(loadingNotification);
      
      html2pdf().set(opt).from(element).save().then(() => {
        // Remove loading state on success
        document.body.removeChild(loadingNotification);
      }).catch(error => {
        console.error('PDF generation failed:', error);
        document.body.removeChild(loadingNotification);
        
        // Show error notification
        const errorNotification = document.createElement('div');
        errorNotification.className = 'fixed bottom-4 right-4 bg-red-600 text-white py-2 px-4 rounded-md shadow';
        errorNotification.textContent = 'PDF generation failed. Please try again.';
        document.body.appendChild(errorNotification);
        
        // Remove error notification after 3 seconds
        setTimeout(() => {
          document.body.removeChild(errorNotification);
        }, 3000);
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Could not generate PDF. Please try again later.');
    }
  };
  
  // Define readiness descriptions based on scores
  const getReadinessDescription = (score, type) => {
    if (type === 'emotional') {
      if (score >= 80) return "You have high emotional readiness for succession.";
      if (score >= 50) return "You have moderate emotional readiness, but may need to prepare for identity changes.";
      return "You have significant emotional attachments to your business that need addressing.";
    } else if (type === 'financial') {
      if (score >= 80) return "You're financially well-prepared for transition.";
      if (score >= 50) return "You have moderate financial readiness, but should strengthen your position.";
      return "You need to focus on building financial resources outside your business.";
    } else {
      if (score >= 80) return "You're well-positioned for succession planning.";
      if (score >= 50) return "You have a foundation for succession but need to address key areas.";
      return "You should focus on foundational succession planning elements.";
    }
  };
  
  return (
    <div className="flex h-screen overflow-hidden bg-succession-bg-secondary transition-theme">
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-succession-text-primary transition-theme">Succession Planning Summary</h1>
                </div>
                <button
                  type="button"
                  onClick={onRevise}
                  className="inline-flex items-center px-4 py-2 border border-succession-border text-sm font-medium rounded-md shadow-sm text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-theme"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Questionnaire
                </button>
              </div>
              
              {/* Prominent Completion Banner */}
              <div className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-5 sm:px-8 sm:py-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between">
                    <div className="mb-4 sm:mb-0 text-center sm:text-left">
                      <div className="flex items-center mb-2">
                        <CheckCircleIcon className="h-6 w-6 text-indigo-600 mr-2" />
                        <h2 className="text-xl font-bold text-indigo-800">Succession Roadmap Ready!</h2>
                      </div>
                      <p className="text-lg font-medium text-indigo-700">Your personalized succession planning roadmap is complete.</p>
                      <p className="mt-1 text-sm text-indigo-600">
                        Review your readiness scores and recommendations to prepare for a successful transition.
                      </p>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={handleDownloadPDF}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        Download Roadmap PDF
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section Readiness Scores */}
              <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
                <div className="px-4 py-5 sm:px-6 bg-gray-50">
                  <h2 className="text-lg font-medium text-gray-900">Succession Readiness by Section</h2>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    A detailed breakdown of your readiness in each area of succession planning.
                  </p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  <SectionReadinessScores formData={formData} />
                </div>
              </div>
              
              {/* AI Readiness Scores Section */}
              {aiInsights && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
                  <div className="px-4 py-5 sm:px-6 bg-gray-50">
                    <h2 className="text-lg font-medium text-gray-900">AI Assessment</h2>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                      AI-powered analysis of your emotional and financial readiness for succession.
                    </p>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <ReadinessScoreCard 
                        title="Emotional Readiness"
                        score={aiInsights.emotional_readiness_score}
                        description={getReadinessDescription(aiInsights.emotional_readiness_score, 'emotional')}
                        icon={ChartBarIcon}
                        bgColor="bg-blue-50"
                        iconBgColor="bg-blue-500"
                        textColor="text-blue-800"
                      />
                      
                      <ReadinessScoreCard 
                        title="Financial Readiness"
                        score={aiInsights.financial_readiness_score}
                        description={getReadinessDescription(aiInsights.financial_readiness_score, 'financial')}
                        icon={ChartBarIcon}
                        bgColor="bg-green-50"
                        iconBgColor="bg-green-500"
                        textColor="text-green-800"
                      />
                      
                      <ReadinessScoreCard 
                        title="Overall AI Assessment"
                        score={aiInsights.overall_readiness_score}
                        description={getReadinessDescription(aiInsights.overall_readiness_score, 'overall')}
                        icon={ChartBarIcon}
                        bgColor="bg-purple-50"
                        iconBgColor="bg-purple-500"
                        textColor="text-purple-800"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* AI Insights Section */}
              {aiInsights && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
                  <div className="px-4 py-5 sm:px-6 bg-gray-50">
                    <h2 className="text-lg font-medium text-gray-900">AI-Generated Insights</h2>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                      Personalized insights to help guide your succession planning journey.
                    </p>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <InsightCard 
                        title="Key Strengths"
                        content={aiInsights.key_strengths}
                        icon={CheckCircleIcon}
                        bgColor="bg-green-50"
                        iconBgColor="bg-green-500"
                        textColor="text-green-800"
                      />
                      
                      <InsightCard 
                        title="Identified Risks"
                        content={aiInsights.identified_risks}
                        icon={ExclamationTriangleIcon}
                        bgColor="bg-yellow-50"
                        iconBgColor="bg-yellow-500"
                        textColor="text-yellow-800"
                      />
                      
                      <InsightCard 
                        title="Action Recommendations"
                        content={aiInsights.action_recommendations}
                        icon={ArrowPathIcon}
                        bgColor="bg-blue-50"
                        iconBgColor="bg-blue-500"
                        textColor="text-blue-800"
                      />
                      
                      <InsightCard 
                        title="Planning Status"
                        content={aiInsights.compliance_status}
                        icon={CheckCircleIcon}
                        bgColor="bg-purple-50"
                        iconBgColor="bg-purple-500"
                        textColor="text-purple-800"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Summary Content Section */}
              <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
                <div className="px-4 py-5 sm:px-6 bg-gray-50">
                  <h2 className="text-lg font-medium text-gray-900">Succession Plan Summary</h2>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    A summary of key information from your questionnaire responses.
                  </p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  <div className="prose prose-indigo max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {summaryContent}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
              
              {/* Preview Modal */}
              {showModal && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                  <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    {/* Background overlay */}
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowModal(false)}></div>
                    
                    {/* Modal panel */}
                    <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                      <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                            Succession Planning Roadmap Preview
                          </h3>
                          <button
                            type="button"
                            className="text-gray-400 hover:text-gray-500"
                            onClick={() => setShowModal(false)}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="bg-gray-50 rounded-md p-4 max-h-[70vh] overflow-y-auto">
                          <div className="prose prose-sm prose-indigo max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {summaryContent}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                          type="button"
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={() => setShowModal(false)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Hidden div for PDF generation */}
              <div className="hidden">
                <div ref={pdfRef} className="p-8 bg-white" style={{ width: '8.5in', maxWidth: '8.5in', fontFamily: 'Arial, sans-serif' }}>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h1 className="text-3xl font-bold mb-2">Succession Planning Roadmap</h1>
                    <p className="text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
                  </div>
                  
                  <hr className="my-4 border-t border-gray-300" />

                  {/* Section Readiness Scores for PDF */}
                  <div style={{ marginBottom: '30px' }}>
                    <h2 style={{ fontSize: '18pt', marginBottom: '15px' }}>Section Readiness Assessment</h2>
                    
                    {/* This will be rendered by html2pdf with the current state of scores */}
                    <div style={{ maxWidth: '400px', margin: '0 auto 20px' }}>
                      <SectionReadinessScores formData={formData} />
                    </div>
                  </div>
                  
                  {/* Readiness Scores for PDF */}
                  {aiInsights && (
                    <div style={{ marginBottom: '20px' }}>
                      <h2 style={{ fontSize: '18pt', marginBottom: '10px' }}>AI Readiness Assessment</h2>
                      
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                        <tbody>
                        <tr>
                          <th className="text-left p-2 border border-succession-border bg-succession-bg-tertiary text-succession-text-primary">Category</th>
                          <th className="text-center p-2 border border-succession-border bg-succession-bg-tertiary text-succession-text-primary">Score</th>
                          <th className="text-left p-2 border border-succession-border bg-succession-bg-tertiary text-succession-text-primary">Description</th>
                        </tr>
                        <tr>
                          <td className="p-2 border border-succession-border text-succession-text-primary">Emotional Readiness</td>
                          <td className="text-center p-2 border border-succession-border text-succession-text-primary">{aiInsights.emotional_readiness_score}/100</td>
                          <td className="p-2 border border-succession-border text-succession-text-primary">{getReadinessDescription(aiInsights.emotional_readiness_score, 'emotional')}</td>
                        </tr>
                        <tr>
                          <td className="p-2 border border-succession-border text-succession-text-primary">Financial Readiness</td>
                          <td className="text-center p-2 border border-succession-border text-succession-text-primary">{aiInsights.financial_readiness_score}/100</td>
                          <td className="p-2 border border-succession-border text-succession-text-primary">{getReadinessDescription(aiInsights.financial_readiness_score, 'financial')}</td>
                        </tr>
                        <tr>
                          <td className="p-2 border border-succession-border text-succession-text-primary font-bold">Overall AI Assessment</td>
                          <td className="text-center p-2 border border-succession-border text-succession-text-primary">{aiInsights.overall_readiness_score}/100</td>
                          <td className="p-2 border border-succession-border text-succession-text-primary">{getReadinessDescription(aiInsights.overall_readiness_score, 'overall')}</td>
                        </tr>
                        </tbody>
                      </table>

                      {/* AI Insights for PDF */}
                      <h2 style={{ fontSize: '18pt', marginBottom: '10px', marginTop: '30px' }}>Key Insights</h2>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold text-succession-success mb-2">Key Strengths</h3>
                        <p className="text-sm text-succession-success">{aiInsights.key_strengths}</p>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold text-succession-warning mb-2">Identified Risks</h3>
                        <p className="text-sm text-succession-warning">{aiInsights.identified_risks}</p>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold text-succession-primary mb-2">Action Recommendations</h3>
                        <p className="text-sm text-succession-primary">{aiInsights.action_recommendations}</p>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold text-succession-secondary mb-2">Planning Status</h3>
                        <p className="text-sm text-succession-secondary">{aiInsights.compliance_status}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="prose max-w-none" style={{ fontSize: '12pt', lineHeight: '1.5' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {summaryContent}
                    </ReactMarkdown>
                  </div>
                  
                  <div style={{ marginTop: '40px', fontSize: '10pt', color: '#666', textAlign: 'center' }}>
                    <p>This document was automatically generated from the Succession Planning Questionnaire.</p>
                    <p>© {new Date().getFullYear()} FindBob. All rights reserved.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SuccessionPlanningSummaryPage; 